import { useReducer } from "react";
import { ICartAddressDetails } from "@/api_framework/api_modals/Cart";

type ActiveModalType = "addressList" | "addAddress" | "addressClusterChange" | null;

interface AddressSelectionState {
  activeModal: ActiveModalType;
  selectedAddress: ICartAddressDetails | null;
  selectedAddressId: string | null;
  currentAddress: any;
  pendingAddress: ICartAddressDetails | null;
  clusterResponse: any;
  showClusterChangeModal: boolean;
}

type AddressSelectionAction =
  | { type: "SET_ACTIVE_MODAL"; payload: ActiveModalType }
  | { type: "SET_ACTIVE_MODAL_WITH_CLUSTER_FLAG"; payload: ActiveModalType }
  | { type: "SET_SELECTED_ADDRESS"; payload: ICartAddressDetails | null }
  | { type: "SET_SELECTED_ADDRESS_ID"; payload: string | null }
  | { type: "SET_CURRENT_ADDRESS"; payload: any }
  | { type: "SET_PENDING_ADDRESS"; payload: ICartAddressDetails | null }
  | { type: "SET_CLUSTER_RESPONSE"; payload: any }
  | { type: "SET_SHOW_CLUSTER_CHANGE_MODAL"; payload: boolean };

const initialAddressSelectionState: AddressSelectionState = {
  activeModal: null,
  selectedAddress: null,
  selectedAddressId: null,
  currentAddress: null,
  pendingAddress: null,
  clusterResponse: null,
  showClusterChangeModal: false,
};

function addressSelectionReducer(
  state: AddressSelectionState,
  action: AddressSelectionAction,
): AddressSelectionState {
  switch (action.type) {
    case "SET_ACTIVE_MODAL":
      return { ...state, activeModal: action.payload };
    case "SET_ACTIVE_MODAL_WITH_CLUSTER_FLAG":
      return {
        ...state,
        activeModal: action.payload,
        showClusterChangeModal: action.payload === "addressClusterChange",
      };
    case "SET_SELECTED_ADDRESS":
      return { ...state, selectedAddress: action.payload };
    case "SET_SELECTED_ADDRESS_ID":
      return { ...state, selectedAddressId: action.payload };
    case "SET_CURRENT_ADDRESS":
      return { ...state, currentAddress: action.payload };
    case "SET_PENDING_ADDRESS":
      return { ...state, pendingAddress: action.payload };
    case "SET_CLUSTER_RESPONSE":
      return { ...state, clusterResponse: action.payload };
    case "SET_SHOW_CLUSTER_CHANGE_MODAL":
      return { ...state, showClusterChangeModal: action.payload };
    default:
      return state;
  }
}

// Groups the address-selection + delivery-cluster-change flow (which modal is open, the
// selected/current/pending address, and the cluster-change response) since several handlers
// need to update more than one of these fields atomically.
export const useAddressSelectionState = () => {
  const [state, dispatch] = useReducer(addressSelectionReducer, initialAddressSelectionState);

  return {
    activeModal: state.activeModal,
    setActiveModal: (payload: ActiveModalType) => dispatch({ type: "SET_ACTIVE_MODAL", payload }),
    setActiveModalWithClusterFlag: (payload: ActiveModalType) =>
      dispatch({ type: "SET_ACTIVE_MODAL_WITH_CLUSTER_FLAG", payload }),
    selectedAddress: state.selectedAddress,
    setSelectedAddress: (payload: ICartAddressDetails | null) => dispatch({ type: "SET_SELECTED_ADDRESS", payload }),
    selectedAddressId: state.selectedAddressId,
    setSelectedAddressId: (payload: string | null) => dispatch({ type: "SET_SELECTED_ADDRESS_ID", payload }),
    currentAddress: state.currentAddress,
    setCurrentAddress: (payload: any) => dispatch({ type: "SET_CURRENT_ADDRESS", payload }),
    pendingAddress: state.pendingAddress,
    setPendingAddress: (payload: ICartAddressDetails | null) => dispatch({ type: "SET_PENDING_ADDRESS", payload }),
    clusterResponse: state.clusterResponse,
    setClusterResponse: (payload: any) => dispatch({ type: "SET_CLUSTER_RESPONSE", payload }),
    showClusterChangeModal: state.showClusterChangeModal,
    setShowClusterChangeModal: (payload: boolean) => dispatch({ type: "SET_SHOW_CLUSTER_CHANGE_MODAL", payload }),
  };
};
